import Input, { InputProps } from "@mui/joy/Input";
import { styled } from "@mui/joy/styles";
import * as React from "react";
import { IMask, IMaskInput } from "react-imask";
import styles from "./FloatLabelInput.module.css";
const style = {
  border: "none",
  minWidth: 0,
  outline: 0,
  padding: 0,
  paddingTop: "1em",
  flex: 1,
  color: "inherit",
  backgroundColor: "transparent",
  fontFamily: "inherit",
  fontSize: "inherit",
  fontStyle: "inherit",
  fontWeight: "inherit",
  lineHeight: "inherit",
  textOverflow: "ellipsis",

  "&::placeholder": {
    opacity: 0,
    transition: "0.1s ease-out",
  },
  "&:focus::placeholder": {
    opacity: 1,
  },
  "&:focus ~ label, &:not(:placeholder-shown) ~ label, &:-webkit-autofill ~ label":
  {
    top: "0.5rem",
    fontSize: "10px",
  },
  "&:focus ~ label": {
    color: "#9AA9B3",
  },
  "&:-webkit-autofill": {
    alignSelf: "stretch",
  },
  "&:-webkit-autofill:not(* + &)": {
    marginInlineStart: "calc(-1 * var(--Input-paddingInline))",
    paddingInlineStart: "var(--Input-paddingInline)",
    borderTopLeftRadius:
      "calc(var(--Input-radius) - var(--variant-borderWidth, 0px))",
    borderBottomLeftRadius:
      "calc(var(--Input-radius) - var(--variant-borderWidth, 0px))",
  },
};
const MaskedStyledInput = styled(
  React.forwardRef<HTMLInputElement, any>((props, ref) => (
    <IMaskInput {...props} inputRef={ref} />
  ))
)({ ...style });
const StyledInput = styled("input")({ ...style });

const StyledLabel = styled("label")(({ theme }) => ({
  position: "absolute",
  lineHeight: 1,
  top: "calc((var(--Input-minHeight) - 1em) / 2)",
  color: theme.vars.palette.text.tertiary,
  fontWeight: theme.vars.fontWeight.md,
  transition: "all 150ms cubic-bezier(0.4, 0, 0.2, 1)",
}));

const InnerInput = React.forwardRef<
  HTMLInputElement,
  React.JSX.IntrinsicElements["input"]
>(function InnerInput(props: any, ref) {
  const id = React.useId();

  const InputComponent = props.maskType ? MaskedStyledInput : StyledInput;
  return (
    <React.Fragment>
      <InputComponent
        {...props}
        ref={ref}
        id={id}
        {...(props.maskType === "tel" && {
          mask: "+55 00 00000-0000",
          definitions: {
            "#": /[0-9]/,
          },
          onAccept: (value: any) => {
            props.onChange({ target: { name: props.name, value } });
          },

          overwrite: true,
        })}
        {...(props.maskType === "cpf" && {
          mask: "000.000.000-00",
          definitions: {
            "#": /[0-9]/,
          },
          onAccept: (value: any) => {
            props.onChange({ target: { name: props.name, value } });
          },

          overwrite: true,
        })}
        {...(props.maskType === "date" && {
          mask: "d/`m/`Y",
          autofix: true,
          blocks: {
            d: {
              mask: IMask.MaskedRange,
              from: 1,
              to: 31,
              maxLength: 2,
            },
            m: {
              mask: IMask.MaskedRange,
              from: 1,
              to: 12,
              maxLength: 2,
            },
            Y: {
              mask: IMask.MaskedRange,
              from: 1900,
              to: 2999,
              maxLength: 4,
            },
          },
          onAccept: (value: any) => {
            props.onChange({ target: { name: props.name, value } });
          },

          overwrite: true,
        })}
      />
      <StyledLabel htmlFor={id}>{props.label}</StyledLabel>
    </React.Fragment>
  );
});
interface FloatingLabelInputProps extends InputProps {
  maskType?: "tel" | "date" | "cpf";
  label?: string;
  maxLength?: number;
}

export default function FloatingLabelInput(props: FloatingLabelInputProps) {
  const { sx: sxProp, readOnly, ...rest } = props;
  const getPlaceholder = (maskType: string | undefined) => {
    if (maskType === "tel") {
      return "+55 00 00000-0000";
    }
    if (maskType === "date") {
      return "dd/mm/yyyy";
    }
    if (maskType === "cpf") {
      return "000.000.000-00";
    }
    return "";
  };

  return (
    <Input
      className={styles.FloatLabelInput}
      {...rest}
      slots={{ input: InnerInput }}
      slotProps={{
        input: {
          placeholder: getPlaceholder(props.maskType) || props.label,
          label: props.label,
          maskType: props.maskType,
          type: props.type,
          maxLength: props.maxLength,
          readOnly: readOnly,
        },
      }}
      sx={[
        {
          "--Input-minHeight": "50px",
          "&::before": {
            display: "none",
          },
        },
        // Merge any caller-provided sx (canonical MUI array form).
        ...(Array.isArray(sxProp) ? sxProp : sxProp ? [sxProp] : []),
      ]}
    />
  );
}
